<template>
  <div
    class="rs-collapse"
    :class="{ 'accordion-row rs-card': accordionCard }"
  >
    <div
      v-b-toggle="auto ? id : null"
      @click="$emit('clicked')"
    >
      <slot
        v-if="!accordionCard"
        name="header"
      />
      <header
        v-else
        class="rs-card__header"
      >
        <div
          v-if="title"
          class="accordion-row__title"
        >
          <div
            v-if="!notDraggable"
            class="column-drag-handle"
          >
            <img src="/img/icon-grip.svg">
          </div>
          <RsIcon
            v-if="icon"
            :icon="icon"
            :weight="weight"
          />
          <h4>{{ $utils.decodeHtml(title) }}</h4>
        </div>
        <slot name="header" />
        <RsIcon
          class="spin"
          weight="far"
          icon="chevron-down"
        />
      </header>
    </div>
    <BCollapse
      :id="id"
      role="tabpanel"
      :accordion="accordion"
      :visible="visible"
    >
      <div
        v-if="accordionCard"
        :class="{ 'rs-card__body': !noBody }"
      >
        <slot name="content" />
      </div>
      <slot
        v-else
        name="content"
      />

      <div
        v-if="accordionCard && hasFooterSlot"
        class="rs-card__footer"
      >
        <slot name="footer" />
      </div>
    </BCollapse>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'RsCollapse',

  props: {
    id: {
      type: String,
      required: true,
      default: 'accordion',
    },
    /**
      * Add accordion styling
      */
    accordionCard: {
      type: Boolean,
      default: false,
    },

    accordion: {
      type: String,
      default: '',
    },
    /**
      * When 'true', expands the collapse
      */
    visible: {
      type: Boolean,
      default: false,
    },
    /**
      * Remove `rs-body` class
      */
    noBody: {
      type: Boolean,
      default: false,
    },
    /**
      * Remove grip icon and required class for draggable component.
      */
    notDraggable: {
      type: Boolean,
      default: false,
    },
    /**
      * The header title text.
      */
    title: String,
    /**
      * The header icon.
      */
    icon: {
      type: String,
    },
    /**
      * The header icon weight.
      */
    weight: {
      type: String,
      default: 'far',
    },
    /**
     * The auto toggler
     */
    auto: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    hasHeaderSlot(): boolean {
      return !!(this.$slots as any)?.header;
    },
    hasFooterSlot(): boolean {
      return !!(this.$slots as any)?.footer;
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';
.rs-collapse{
  & > div{
    outline: 0;
  }
  i{
    transition: transform 0.2s ease;
  }

  &:last-child {
    .rs-card__header {
      border-top-color: #eaeaea;
    }
    .collapsed {
      .rs-card__header {
        border-bottom-color: transparent;
      }
    }
    .collapse.show {
      border-bottom-color: transparent;
    }
  }
}
.accordion-row {
  margin-bottom: 1rem;

  .column-drag-handle{
    margin-right: 1rem;
  }
  &__title{
    display: flex;
    align-items: center;
    .column-drag-handle{
      .rs-icon{
        margin-right: 1rem;
        color: $tertiary-gray;
      }
    }
    .rs-icon{
      color: $secondary-color-dark;
      margin-right: 0.5rem;
    }
    h4{
      font-size: 1rem;
      font-weight: 600;
    }
  }
  .spin{
    color: $tertiary-gray;
  }
  .collapsed {
    border-radius: $global-radius;
    .rs-card__header{
      border-bottom: none;
    }
  }
  *[aria-expanded="true"] {
    .rs-card__header {
      border-bottom: $card-border;
    }
    .spin {
      transform: rotate(180deg);
    }
  }
  .rs-card__body{
    padding: 1rem 1.5rem;
  }
  .rs-card__footer{
    justify-content: end;
  }
}
</style>
